import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit'
import {
  fetchAllProviders,
  addProvider,
  updateProvider,
  deleteProvider,
  updateProviderPhoto,
} from '../actions/providermanagement.action'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '../../utils/fullname.util'

type ProviderState = {
  data: Provider[]
  loading: boolean
  error: string | null
  successMessage: string | null
  viewProvider: Provider | null
  selectedProviders: Provider[]
}

const initialState: ProviderState = {
  data: [],
  loading: false,
  error: null,
  successMessage: null,
  viewProvider: null,
  selectedProviders: [],
}

// Helper function to sort providers by full name
const sortProvidersByName = (providers: Provider[]): Provider[] => {
  return [...providers].sort((a, b) => {
    const nameA = getFullName(a).toLowerCase()
    const nameB = getFullName(b).toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null
      state.successMessage = null
    },
    setViewProvider: (state, action: PayloadAction<Provider | null>) => {
      const provider = action.payload
      state.viewProvider = provider
      if (provider) {
        const index = state.selectedProviders.findIndex(
          (p) => p.provider_id === provider.provider_id,
        )
        if (index !== -1) {
          state.selectedProviders[index] = provider
        } else {
          state.selectedProviders = [...state.selectedProviders, provider]
        }
      }
    },
    removeSelectedProvider: (state, action: PayloadAction<string>) => {
      const providerId = action.payload
      if (state.viewProvider?.provider_id === providerId) {
        state.viewProvider = null
      }
      state.selectedProviders = state.selectedProviders.filter(
        (p) => p.provider_id !== providerId,
      )
    },
    clearSelectedProviders: (state) => {
      state.selectedProviders = []
      state.viewProvider = null
    },
    restoreProviderSelection: (
      state,
      action: PayloadAction<{
        viewProvider: Provider | null
        selectedProviders: Provider[]
      }>,
    ) => {
      state.viewProvider = action.payload.viewProvider
      state.selectedProviders = action.payload.selectedProviders
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchAllProviders.fulfilled,
        (state, action: PayloadAction<Provider[]>) => {
          state.data = sortProvidersByName(action.payload)
          state.loading = false
          state.successMessage = 'Staff loaded successfully'
        },
      )
      .addCase(
        addProvider.fulfilled,
        (
          state,
          action: PayloadAction<{
            provider: Provider
            selectedClinicId: string | undefined
          }>,
        ) => {
          const { provider, selectedClinicId } = action.payload

          // Only add to list if the clinic ID matches the selected clinic
          if (selectedClinicId && provider.clinic_id === selectedClinicId) {
            state.data.push(provider)
            state.data = sortProvidersByName(state.data)
          }

          state.loading = false
          state.successMessage = 'Staff added successfully'
        },
      )
      .addCase(
        updateProvider.fulfilled,
        (state, action: PayloadAction<Provider>) => {
          const index = state.data.findIndex(
            (provider) => provider.provider_id === action.payload.provider_id,
          )
          if (index !== -1) {
            state.data[index] = action.payload
          }

          // Update viewProvider if it's the same as the updated provider
          if (
            state.viewProvider &&
            state.viewProvider.provider_id === action.payload.provider_id
          ) {
            state.viewProvider = action.payload
          }

          // Update selectedProviders if it contains the updated provider
          const selectedIndex = state.selectedProviders.findIndex(
            (p) => p.provider_id === action.payload.provider_id,
          )
          if (selectedIndex !== -1) {
            state.selectedProviders[selectedIndex] = action.payload
          }

          // Sort providers after update
          state.data = sortProvidersByName(state.data)

          state.loading = false
          state.successMessage = 'Staff updated successfully'
        },
      )
      .addCase(
        updateProviderPhoto.fulfilled,
        (state, action: PayloadAction<Provider>) => {
          const index = state.data.findIndex(
            (provider) => provider.provider_id === action.payload.provider_id,
          )
          if (index !== -1) {
            state.data[index] = action.payload
          }

          if (
            state.viewProvider &&
            state.viewProvider.provider_id === action.payload.provider_id
          ) {
            state.viewProvider = action.payload
          }

          const selectedIndex = state.selectedProviders.findIndex(
            (p) => p.provider_id === action.payload.provider_id,
          )
          if (selectedIndex !== -1) {
            state.selectedProviders[selectedIndex] = action.payload
          }

          state.loading = false
          state.successMessage = 'Staff photo updated successfully'
        },
      )
      .addCase(
        deleteProvider.fulfilled,
        (state, action: PayloadAction<string>) => {
          const providerId = action.payload
          if (state.viewProvider?.provider_id === providerId) {
            state.viewProvider = null
          }
          state.selectedProviders = state.selectedProviders.filter(
            (p) => p.provider_id !== providerId,
          )
          state.data = state.data.filter(
            (provider) => provider.provider_id !== providerId,
          )
          // Sort providers after deletion (though order should remain the same)
          state.data = sortProvidersByName(state.data)
          state.loading = false
          state.successMessage = 'Staff removed successfully'
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith('providers/') &&
          action.type.endsWith('/pending'),
        (state) => {
          state.loading = true
          state.error = null
          state.successMessage = null
        },
      )
      .addMatcher(
        (action: AnyAction) =>
          action.type.startsWith('providers/') &&
          action.type.endsWith('/rejected'),
        (state, action: AnyAction) => {
          state.loading = false
          state.error = action.error?.message || 'Action failed'
          state.successMessage = null
        },
      )
  },
})

export const {
  clearMessages,
  setViewProvider,
  removeSelectedProvider,
  clearSelectedProviders,
  restoreProviderSelection,
} = providerSlice.actions

export default providerSlice.reducer
